// src/hooks/useStockMovements.ts - Hooks pour la gestion des mouvements de stock

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { trackUsage } from '../utils/usageTracking';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { StockMovement, StockMovementType, InventoryFilters } from '../types/inventory';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useDefaultPointOfSaleSite, useSites } from './useSites';
import { useCreateStockNotification } from './useNotifications';
import { getStockAlertThresholdForSite } from '../utils/stockAlerts';
import { matchesSearchInFields } from '../utils/searchUtils';
import { getProductDisplayName } from '../utils/productPackaging';
import { getMissingDefaultSiteError } from '../utils/siteDefaults';
import { getProductStockAtSite } from '../utils/productStock';

// ===== HOOKS POUR LES MOUVEMENTS DE STOCK =====

export const useStockMovements = (filters?: InventoryFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'stockMovements', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.stockMovements),
          where('businessId', '==', businessId),
          orderBy('date', 'desc'),
          limit(100) // Limiter pour les performances
        );

        // Filtre par employé
        if (filters?.employeeId) {
          q = query(q, where('employeeId', '==', filters.employeeId));
        }

        // Ne pas filtrer `type` dans Firestore : évite un index composite
        // businessId + type + date (sinon la page Pertes peut renvoyer [] silencieusement).

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useStockMovements - Main query');
          throw error;
        });
        
        let movements = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            productId: data.productId,
            productName: data.productName,
            type: data.type,
            quantity: data.quantity,
            unitCost: data.unitCost,
            totalCost: data.totalCost,
            reason: data.reason,
            lossReason: data.lossReason,
            reference: data.reference,
            date: data.date?.toDate(),
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            notes: data.notes,
            createdAt: data.createdAt?.toDate(),
            deliveryId: data.deliveryId,
            siteId: data.siteId,
            siteName: data.siteName,
            externalDispatchId: data.externalDispatchId,
            externalDestinationName: data.externalDestinationName,
            externalDestinationContact: data.externalDestinationContact,
            externalReference: data.externalReference,
          } as StockMovement;
        });

        // Filtres côté client
        if (filters?.movementType) {
          movements = movements.filter((movement) => movement.type === filters.movementType);
        }

        if (filters?.dateRange) {
          const [startDate, endDate] = filters.dateRange;
          movements = movements.filter(movement => 
            movement.date >= startDate && movement.date <= endDate
          );
        }

        if (filters?.searchTerm) {
          movements = movements.filter(movement =>
            matchesSearchInFields(
              [
                movement.productName,
                movement.reason,
                movement.reference,
                movement.employeeName,
              ],
              filters.searchTerm!
            )
          );
        }

        return movements;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des mouvements:', error);
        return [];
      }
    },
    enabled:
      !!businessId &&
      (!!permissions?.canViewStockMovements ||
        !!permissions?.canViewInventoryReports ||
        !!permissions?.canViewInventoryLosses),
  });
};

export const useAddStockMovement = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile, user, isSuperAdmin } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;
  const defaultPosSite = useDefaultPointOfSaleSite(businessId);
  const createStockNotification = useCreateStockNotification();
  
  return useMutation({
    mutationFn: async (movementData: {
      productId: string;
      type: StockMovementType;
      quantity: number;
      unitCost?: number;
      reason: string;
      lossReason?: import('../types/inventory').StockLossReason;
      reference?: string;
      date: Date;
      notes?: string;
      siteId?: string;
      siteName?: string;
    }) => {
      if (!businessId || !userProfile) {
        throw new Error('Institut ou utilisateur non défini');
      }
      if (!isSuperAdmin && permissions?.canCreateStockMovements !== true) {
        throw new Error('Permission insuffisante pour créer un mouvement de stock.');
      }

      // Vérifier que le produit existe et appartient à l'institut
      const productRef = doc(db, COLLECTIONS.products, movementData.productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new Error('Produit introuvable');
      }
      
      const productData = productSnap.data();
      if (productData.businessId !== businessId) {
        throw new Error('Ce produit n\'appartient pas à votre institut');
      }

      const currentStock = productData.currentStock || 0;
      const stockBySite = (productData.stockBySite as Record<string, number>) || {};
      const resolvedSiteId = movementData.siteId || defaultPosSite?.id;
      const resolvedSiteName = movementData.siteName || defaultPosSite?.name;
      if (!resolvedSiteId || !resolvedSiteName) {
        throw new Error(getMissingDefaultSiteError(currentBusiness));
      }
      const currentStockAtSite = getProductStockAtSite(
        { currentStock, stockBySite },
        resolvedSiteId
      );
      
      // Vérifier que la sortie / perte ne dépasse pas le stock disponible
      if (
        (movementData.type === 'exit' || movementData.type === 'loss') &&
        movementData.quantity > currentStockAtSite
      ) {
        throw new Error(
          `Stock insuffisant. Stock actuel: ${currentStockAtSite}, quantité demandée: ${movementData.quantity}`
        );
      }

      // Calculer le nouveau stock selon le type de mouvement
      let newStock: number;
      let newStockAtSite: number | undefined;
      switch (movementData.type) {
        case 'entry':
          newStock = currentStock + movementData.quantity;
          if (resolvedSiteId) {
            newStockAtSite = currentStockAtSite + movementData.quantity;
          }
          break;
        case 'exit':
        case 'loss':
          newStock = currentStock - movementData.quantity;
          if (resolvedSiteId) {
            newStockAtSite = currentStockAtSite - movementData.quantity;
          }
          break;
        case 'adjustment':
          // Pour les ajustements, la quantité représente le nouveau stock souhaité
          if (resolvedSiteId) {
            const delta = movementData.quantity - currentStockAtSite;
            newStock = currentStock + delta;
            newStockAtSite = movementData.quantity;
          } else {
            newStock = movementData.quantity;
          }
          break;
        default:
          throw new Error('Type de mouvement invalide');
      }

      // Empêcher le stock négatif
      newStock = Math.max(0, newStock);

      // Utiliser une transaction pour garantir la cohérence
      const batch = writeBatch(db);

      // Ajouter le mouvement de stock
      const movementRef = doc(collection(db, COLLECTIONS.stockMovements));
      const newMovement: any = {
        businessId,
        productId: movementData.productId,
        productName: getProductDisplayName(productData),
        type: movementData.type,
        quantity: movementData.quantity,
        reason: movementData.reason.trim(),
        date: Timestamp.fromDate(movementData.date),
        employeeId: userProfile.id,
        employeeName: userProfile.name,
        createdAt: Timestamp.now(),
        ...(user?.uid ? { createdByUserId: user.uid } : {}),
      };
      
      // Coût unitaire / total — toujours pour les pertes (sinon le dashboard affiche 0 €)
      const resolvedUnitCost =
        movementData.unitCost != null && Number.isFinite(Number(movementData.unitCost))
          ? Number(movementData.unitCost)
          : movementData.type === 'loss'
            ? Number(productData.averageCost) || Number(productData.costPrice) || 0
            : undefined;
      if (resolvedUnitCost != null) {
        newMovement.unitCost = resolvedUnitCost;
        newMovement.totalCost = movementData.quantity * resolvedUnitCost;
      }
      
      if (movementData.reference?.trim()) {
        newMovement.reference = movementData.reference.trim();
      }
      
      if (movementData.notes?.trim()) {
        newMovement.notes = movementData.notes.trim();
      }
      if (movementData.type === 'loss' && movementData.lossReason) {
        newMovement.lossReason = movementData.lossReason;
      }
      if (resolvedSiteId && resolvedSiteName) {
        newMovement.siteId = resolvedSiteId;
        newMovement.siteName = resolvedSiteName;
      }
      
      batch.set(movementRef, newMovement);

      // Mettre à jour le stock du produit
      const productUpdate: Record<string, unknown> = {
        currentStock: newStock,
        updatedAt: Timestamp.now(),
      };
      if (resolvedSiteId && newStockAtSite !== undefined) {
        productUpdate.stockBySite = {
          ...stockBySite,
          [resolvedSiteId]: Math.max(0, newStockAtSite),
        };
      }
      batch.update(productRef, productUpdate as any);

      await batch.commit();

      try {
        const thresholdAtSite = getStockAlertThresholdForSite(productData, resolvedSiteId);
        await createStockNotification.mutateAsync({
          productId: movementData.productId,
          productName: getProductDisplayName(productData),
          currentStock: Math.max(0, newStockAtSite ?? newStock),
          minStockAlert: thresholdAtSite,
          siteId: resolvedSiteId,
          siteName: resolvedSiteName,
        });
      } catch (error) {
        console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
      }
      
      return movementRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
      trackUsage('stock_movement_created');
    },
  });
};

/**
 * Transfert de stock entre deux sites (ex: Magasin -> Point de vente).
 * Crée une sortie sur le site source, une entrée sur le site destination
 * et met à jour stockBySite + currentStock du produit dans une transaction.
 */
export const useTransferStockBetweenSites = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const { data: sites = [] } = useSites(currentBusiness?.id);
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (params: {
      fromSiteId: string;
      toSiteId: string;
      items: { productId: string; quantity: number }[];
      reason?: string;
      /** Date d'envoi du transfert (mouvements + deliveryDate). Défaut : maintenant. */
      sendDate?: Date;
    }) => {
      if (!businessId || !userProfile) {
        throw new Error('Institut ou utilisateur non défini');
      }
      if (params.fromSiteId === params.toSiteId) {
        throw new Error('Le site source et le site destination doivent être différents');
      }
      if (!params.items || params.items.length === 0) {
        throw new Error('Aucun produit sélectionné pour le transfert');
      }

      const fromSite = sites.find((s) => s.id === params.fromSiteId);
      const toSite = sites.find((s) => s.id === params.toSiteId);
      if (!fromSite || !toSite) {
        throw new Error('Site source ou destination introuvable');
      }

      const now = Timestamp.now();
      const sendTs = params.sendDate ? Timestamp.fromDate(params.sendDate) : now;

      const batch = writeBatch(db);

      const deliveryItems: any[] = [];
      let totalQuantity = 0;
      const notificationPayloads: Array<{
        productId: string;
        productName: string;
        currentStock: number;
        minStockAlert: number;
        siteId?: string;
        siteName?: string;
      }> = [];

      // Pour chaque produit transféré, créer les mouvements et mettre à jour le stock
      for (const item of params.items) {
        if (!item?.productId || !item?.quantity) continue;

        const productRef = doc(db, COLLECTIONS.products, item.productId);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          throw new Error('Produit introuvable');
        }
        const productData = productSnap.data() as any;
        if (productData.businessId !== businessId) {
          throw new Error('Ce produit n\'appartient pas à votre institut');
        }

        const stockBySite: Record<string, number> = productData.stockBySite || {};
        const fromCurrent = stockBySite[params.fromSiteId] ?? 0;
        const toCurrent = stockBySite[params.toSiteId] ?? 0;

        if (item.quantity > fromCurrent) {
          throw new Error(
            `Stock insuffisant sur le site source pour le produit ${productData.name}. Stock disponible: ${fromCurrent}, quantité demandée: ${item.quantity}`
          );
        }

        const newStockBySite: Record<string, number> = {
          ...stockBySite,
          [params.fromSiteId]: Math.max(0, fromCurrent - item.quantity),
          [params.toSiteId]: toCurrent + item.quantity,
        };

        const newCurrentStock = Object.values(newStockBySite).reduce((sum, q) => sum + (q || 0), 0);

        // Mouvement sortie (site source)
        const exitRef = doc(collection(db, COLLECTIONS.stockMovements));
        batch.set(exitRef, {
          businessId,
          productId: item.productId,
          productName: getProductDisplayName(productData),
          type: 'exit' as StockMovementType,
          quantity: item.quantity,
          reason: params.reason?.trim() || (toSite ? `Transfert vers ${toSite.name}` : 'Transfert sortant'),
          date: sendTs,
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          createdAt: sendTs,
          siteId: params.fromSiteId,
          siteName: fromSite?.name,
        });

        // Mouvement entrée (site destination)
        const entryRef = doc(collection(db, COLLECTIONS.stockMovements));
        batch.set(entryRef, {
          businessId,
          productId: item.productId,
          productName: getProductDisplayName(productData),
          type: 'entry' as StockMovementType,
          quantity: item.quantity,
          reason: params.reason?.trim() || (fromSite ? `Transfert depuis ${fromSite.name}` : 'Transfert entrant'),
          date: sendTs,
          employeeId: userProfile.id,
          employeeName: userProfile.name,
          createdAt: sendTs,
          siteId: params.toSiteId,
          siteName: toSite?.name,
        });

        // Mise à jour du produit
        batch.update(productRef, {
          stockBySite: newStockBySite,
          currentStock: newCurrentStock,
          updatedAt: now,
        });

        notificationPayloads.push({
          productId: item.productId,
          productName: getProductDisplayName(productData),
          currentStock: Math.max(0, fromCurrent - item.quantity),
          minStockAlert: getStockAlertThresholdForSite(productData, params.fromSiteId),
          siteId: params.fromSiteId,
          siteName: fromSite.name,
        });
        notificationPayloads.push({
          productId: item.productId,
          productName: getProductDisplayName(productData),
          currentStock: Math.max(0, toCurrent + item.quantity),
          minStockAlert: getStockAlertThresholdForSite(productData, params.toSiteId),
          siteId: params.toSiteId,
          siteName: toSite.name,
        });

        deliveryItems.push({
          productId: item.productId,
          productName: getProductDisplayName(productData),
          quantity: item.quantity,
          unitCost: 0,
          totalCost: 0,
        });
        totalQuantity += item.quantity;
      }

      if (!deliveryItems.length) {
        throw new Error('Aucun produit valide pour le transfert');
      }

      // Créer un Delivery interne pour le site de réception
      const deliveryRef = doc(collection(db, COLLECTIONS.deliveries));
      batch.set(deliveryRef, {
        businessId,
        supplierId: 'internal_transfer',
        supplierName: fromSite.name || 'Transfert interne',
        deliveryDate: sendTs,
        reference: null,
        attachments: [],
        items: deliveryItems,
        totalQuantity,
        totalAmount: 0,
        subtotalExcludingTax: 0,
        totalTaxAmount: 0,
        subtotalIncludingTax: 0,
        taxSummary: [],
        status: 'received',
        employeeId: userProfile.id,
        employeeName: userProfile.name,
        ...(params.reason?.trim() ? { notes: params.reason.trim() } : {}),
        siteId: params.toSiteId,
        siteName: toSite.name,
        fromSiteId: params.fromSiteId,
        fromSiteName: fromSite.name,
        isInternalTransfer: true,
        createdAt: now,
        processedAt: now,
      });

      await batch.commit();
      await Promise.all(
        notificationPayloads.map((payload) =>
          createStockNotification.mutateAsync(payload).catch((error) => {
            console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
          })
        )
      );

      return { deliveryId: deliveryRef.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });
};

/** Suppression d'un mouvement de stock (Super Admin uniquement). Annule l'effet sur le stock pour entrée/sortie. */
export const useDeleteStockMovement = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const createStockNotification = useCreateStockNotification();

  return useMutation({
    mutationFn: async (movement: StockMovement) => {
      if (!businessId) {
        throw new Error('Institut non défini');
      }
      if (movement.businessId !== businessId) {
        throw new Error('Ce mouvement n\'appartient pas à l\'institut actuel');
      }
      const type = movement.type as StockMovementType;
      if (type !== 'entry' && type !== 'exit' && type !== 'loss') {
        throw new Error('Seuls les mouvements de type Entrée, Sortie ou Perte peuvent être supprimés.');
      }

      const productRef = doc(db, COLLECTIONS.products, movement.productId);
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) {
        throw new Error('Produit introuvable');
      }
      const productData = productSnap.data();
      if (productData.businessId !== businessId) {
        throw new Error('Ce produit n\'appartient pas à votre institut');
      }

      const currentStock = productData.currentStock ?? 0;
      const stockBySite = (productData.stockBySite as Record<string, number>) || {};
      let newStock: number;
      if (type === 'entry') {
        newStock = Math.max(0, currentStock - movement.quantity);
      } else {
        // exit / loss : restaurer le stock
        newStock = currentStock + movement.quantity;
      }

      const batch = writeBatch(db);
      const movementRef = doc(db, COLLECTIONS.stockMovements, movement.id);
      batch.delete(movementRef);
      const productUpdate: Record<string, unknown> = {
        currentStock: newStock,
        updatedAt: Timestamp.now(),
      };
      let updatedStockAtSite: number | undefined;
      if (movement.siteId) {
        const currentAtSite = getProductStockAtSite(
          { currentStock, stockBySite },
          movement.siteId
        );
        const newAtSite =
          type === 'entry'
            ? Math.max(0, currentAtSite - movement.quantity)
            : currentAtSite + movement.quantity;
        updatedStockAtSite = newAtSite;
        productUpdate.stockBySite = {
          ...stockBySite,
          [movement.siteId]: newAtSite,
        };
      }
      batch.update(productRef, productUpdate as any);
      await batch.commit();

      try {
        const thresholdAtSite = getStockAlertThresholdForSite(productData, movement.siteId);
        await createStockNotification.mutateAsync({
          productId: movement.productId,
          productName: movement.productName,
          currentStock: Math.max(0, updatedStockAtSite ?? newStock),
          minStockAlert: thresholdAtSite,
          ...(movement.siteId ? { siteId: movement.siteId, siteName: movement.siteName } : {}),
        });
      } catch (error) {
        console.warn('Erreur lors de la synchronisation de la notification de stock:', error);
      }

      return movement.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useStockMovementsByProduct = (productId: string, limitCount?: number) => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'stockMovements', 'byProduct', businessId, productId, limitCount],
    queryFn: async () => {
      if (!productId || !businessId) {
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.stockMovements),
          where('businessId', '==', businessId),
          where('productId', '==', productId),
          orderBy('date', 'desc')
        );

        if (limitCount) {
          q = query(q, limit(limitCount));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useStockMovements - Main query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            productId: data.productId,
            productName: data.productName,
            type: data.type,
            quantity: data.quantity,
            unitCost: data.unitCost,
            totalCost: data.totalCost,
            reason: data.reason,
            reference: data.reference,
            date: data.date?.toDate(),
            employeeId: data.employeeId,
            employeeName: data.employeeName,
            notes: data.notes,
            createdAt: data.createdAt?.toDate(),
            deliveryId: data.deliveryId,
            siteId: data.siteId,
            siteName: data.siteName,
            externalDispatchId: data.externalDispatchId,
            externalDestinationName: data.externalDestinationName,
            externalDestinationContact: data.externalDestinationContact,
            externalReference: data.externalReference,
          } as StockMovement;
        });
        
      } catch (error) {
        console.error('Erreur lors de la récupération des mouvements du produit:', error);
        return [];
      }
    },
    enabled: !!productId && !!businessId,
  });
};

function movementCostFromDoc(data: {
  totalCost?: unknown;
  unitCost?: unknown;
  quantity?: unknown;
}): number {
  const explicit = Number(data.totalCost);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const qty = Number(data.quantity) || 0;
  const unit = Number(data.unitCost) || 0;
  const derived = qty * unit;
  if (derived > 0) return derived;
  return Number.isFinite(explicit) ? explicit : 0;
}

// Hook pour les statistiques de mouvements
export const useStockMovementsStats = (dateRange?: [Date, Date]) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['inventory', 'stockMovements', 'stats', businessId, dateRange],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalEntries: 0,
          totalExits: 0,
          totalAdjustments: 0,
          totalValue: 0,
          totalLossesQty: 0,
          totalLossesCost: 0,
        };
      }

      try {
        // Même stratégie que useStockMovements : pas de filtre date Firestore
        // (évite index / requête composée qui échoue → stats à 0).
        const q = query(
          collection(db, COLLECTIONS.stockMovements),
          where('businessId', '==', businessId),
          orderBy('date', 'desc'),
          limit(500)
        );

        const snapshot = await getDocs(q).catch((error) => {
          handleFirestoreError(error, 'useStockMovementsStats - Main query');
          throw error;
        });

        let totalEntries = 0;
        let totalExits = 0;
        let totalAdjustments = 0;
        let totalValue = 0;
        let totalLossesQty = 0;
        let totalLossesCost = 0;

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const type = data.type as StockMovementType;
          const movementDate: Date | undefined = data.date?.toDate?.();
          if (dateRange && movementDate) {
            const [startDate, endDate] = dateRange;
            if (movementDate < startDate || movementDate > endDate) return;
          }

          const quantity = Number(data.quantity) || 0;
          const totalCost = movementCostFromDoc(data);

          switch (type) {
            case 'entry':
              totalEntries += quantity;
              totalValue += totalCost;
              break;
            case 'exit':
              totalExits += quantity;
              break;
            case 'loss':
              totalLossesQty += quantity;
              totalLossesCost += totalCost;
              break;
            case 'adjustment':
              totalAdjustments += 1;
              break;
          }
        });

        return {
          totalEntries,
          totalExits,
          totalAdjustments,
          totalValue,
          totalLossesQty,
          totalLossesCost,
        };
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques:', error);
        return {
          totalEntries: 0,
          totalExits: 0,
          totalAdjustments: 0,
          totalValue: 0,
          totalLossesQty: 0,
          totalLossesCost: 0,
        };
      }
    },
    enabled:
      !!businessId &&
      (!!permissions?.canViewStockMovements ||
        !!permissions?.canViewInventoryReports ||
        !!permissions?.canViewInventoryLosses),
  });
};